import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, XCircle, CheckCircle2, RefreshCw, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import useRealtime from "@/lib/realtime";
import { formatDateTime } from "@/lib/format";

/**
 * KeycardPanel — door-lock keycard writer widget for the Reservation Drawer.
 *
 * Supports multiple active cards per reservation. Each card can be cancelled
 * individually; a "+ Add card" button issues an extra card.
 *
 * Props:
 *   reservationId
 *   status:      reservation status (confirmed | checked_in | checked_out | ...)
 *   cards:       reservation.key_cards[]  ({snr, issued_at}[])
 *   legacySnr:   reservation.key_card_snr  (backward compat)
 *   onChanged:   () => void   parent should refetch the reservation
 */
export default function KeycardPanel({ reservationId, status, cards, legacySnr, onChanged }) {
    const { t } = useTranslation();
    const [integ, setInteg] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [busy, setBusy] = useState(false);

    const activeCards = (cards && cards.length)
        ? cards
        : (legacySnr ? [{ snr: legacySnr }] : []);

    const load = useCallback(async () => {
        try {
            const [i, j] = await Promise.all([
                api.get("/keys/integration"),
                api.get(`/keys/jobs?reservation_id=${reservationId}&limit=10`),
            ]);
            setInteg(i.data);
            setJobs(j.data || []);
        } catch { /* silent */ }
    }, [reservationId]);

    useEffect(() => { load(); }, [load]);

    useRealtime((msg) => {
        if (msg?.event === "key_job.updated") {
            const j = msg.payload;
            if (jobs.some((x) => x.id === j.id)) {
                load();
                onChanged?.();
                if (j.status === "success") {
                    if (j.action === "issue") toast.success(t("keys.issue_success"));
                    if (j.action === "cancel") toast.success(t("keys.cancel_success"));
                } else if (j.status === "failed") {
                    toast.error(t("keys.job_failed"));
                }
            }
        }
    });

    if (!integ || !integ.enabled) return null;

    const latest = jobs[0];
    const inFlight = latest && (latest.status === "pending" || latest.status === "dispatched");
    const agentOnline = integ.agent_status === "online";

    const enqueue = async (action, snr = null) => {
        setBusy(true);
        try {
            const body = { reservation_id: reservationId, action };
            if (snr) body.card_snr = snr;
            await api.post("/keys/jobs", body);
            toast.success(t("keys.job_queued"));
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };

    const retry = async (jobId) => {
        setBusy(true);
        try {
            await api.post(`/keys/jobs/${jobId}/retry`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const canIssue = ["confirmed", "checked_in"].includes(status);
    const hasCards = activeCards.length > 0;

    return (
        <div className="rounded-md border border-border bg-card p-3" data-testid="keycard-panel">
            <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-300">
                        <KeyRound size={13} />
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        {t("keys.room_key")}
                        {hasCards && <span className="ml-1.5 text-muted-foreground">· {activeCards.length}</span>}
                    </div>
                </div>
                <div className={`flex items-center gap-1 text-[10px] font-medium ${agentOnline ? "text-emerald-600" : "text-muted-foreground"}`}
                     title={integ.agent_last_seen ? formatDateTime(integ.agent_last_seen) : ""}>
                    <span className={`h-1.5 w-1.5 rounded-full ${agentOnline ? "bg-emerald-500" : "bg-slate-400"}`} />
                    {agentOnline ? t("keys.agent_online") : t("keys.agent_offline")}
                </div>
            </div>

            {/* Active cards list */}
            {hasCards && (
                <div className="mb-2 space-y-1">
                    {activeCards.map((c, i) => (
                        <div key={c.snr || i}
                             className="flex items-center justify-between rounded-md bg-emerald-50 px-2.5 py-1.5 text-[12px] text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200">
                            <div className="flex min-w-0 items-center gap-2">
                                <CheckCircle2 size={12} className="shrink-0" />
                                <span className="truncate font-mono">{c.snr}</span>
                                {c.issued_at && (
                                    <span className="text-[10px] text-emerald-700/70 dark:text-emerald-300/70">
                                        · {formatDateTime(c.issued_at)}
                                    </span>
                                )}
                            </div>
                            <button onClick={() => enqueue("cancel", c.snr)}
                                    disabled={busy || inFlight}
                                    data-testid={`keycard-cancel-${c.snr}`}
                                    title={t("keys.cancel")}
                                    className="ml-2 shrink-0 rounded p-0.5 text-emerald-700 hover:bg-red-100 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-emerald-300 dark:hover:bg-red-500/20">
                                <XCircle size={13} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {inFlight && (
                <div className="mb-2 flex items-center gap-2 rounded-md bg-blue-50 px-2.5 py-1.5 text-[12px] text-blue-800 dark:bg-blue-500/10 dark:text-blue-200">
                    <Loader2 size={12} className="animate-spin" />
                    <span>
                        {latest.action === "issue"
                            ? t("keys.waiting_issue")
                            : t("keys.waiting_cancel")}
                    </span>
                </div>
            )}

            {latest && latest.status === "failed" && (
                <div className="mb-2 flex items-start gap-2 rounded-md bg-red-50 px-2.5 py-1.5 text-[12px] text-red-800 dark:bg-red-500/10 dark:text-red-200">
                    <XCircle size={12} className="mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{latest.error || t("keys.failed")}</div>
                        <button onClick={() => retry(latest.id)} className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold underline">
                            <RefreshCw size={10} /> {t("keys.retry")}
                        </button>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-1.5">
                {canIssue && (
                    <button onClick={() => enqueue("issue")} disabled={busy || inFlight}
                            data-testid="keycard-issue-btn"
                            className="inline-flex items-center gap-1 rounded-md bg-orange-500 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50">
                        {hasCards ? <Plus size={11} /> : <KeyRound size={11} />}
                        {hasCards ? t("keys.add_another", "Add another card") : t("keys.issue")}
                    </button>
                )}
                {!agentOnline && (
                    <div className="text-[10px] italic text-muted-foreground">
                        {t("keys.waiting_agent", "Job will queue and dispatch when the agent connects")}
                    </div>
                )}
            </div>
        </div>
    );
}
